import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';
import Modal from '../../../_ui/Modal/Modal';
import { ServiceRequestApi } from '../ServiceRequestApi';
import {
  enableIntegrationForServices,
  serviceRequestModule,
  setIntegrationLoading,
  setIntegrationStatus
} from '../ServiceRequestDucks';
import { changeFormValue } from '../../../passport/Passport';
import { handleError } from '../../../modules/utils/handleError';
import Notice from '../../../modules/utils/Notice';
import DialogContent from '@material-ui/core/DialogContent';
import { Table } from '../ServiceProvidersModal/ServiceProvidersModalStyles';
import IntegrationModal from '../../../components/IntegrationModal/IntegrationModal';
import useSimpleModal from '../../../components/_hooks/useSimpleModal';

function ListChildrenModalForServices({ close, isOpen, data }) {
  const metadataKey = data.code.split('.')[0];
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [listChildren, setListChildren] = useState([]);
  const [loading, setLoading] = useState(false);
  const isChangeRequester = metadataKey === 'request_form_change_requester_short';

  const { loadingIntegration, kdpGbdflStatus, kdpZagsStatus } = useSelector(
    state => state[serviceRequestModule]
  );
  const integrationModal = useSimpleModal();
  const pollingTimeoutRef = useRef(null);
  const [showHelperText, setShowHelperText] = useState(false);

  const requestForChildren = async () => {
    try {
      setLoading(true);
      if (isChangeRequester) {
        const { data } = await ServiceRequestApi.getChildrenFromZagsAndConnectedForVoucher();
        setListChildren(Array.isArray(data) ? data : data.result || []);
        return;
      }
      const { data } = await ServiceRequestApi.getChildrenFromIntegration();
      if (data.status === 'SUCCESS') {
        setListChildren(data.result);
      }
    } catch (error) {
      const errorCode = _.get(error, 'response.data.errors[0].code');
      if (isChangeRequester && errorCode === 'error.tokens_not_found') {
        Notice.error(t('serviceRequest_zagsTokensExpired'));
        integrationModal.open({});
        return;
      }
      handleError(error, t('serviceProviders_exitedErrorListChildren'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    requestForChildren();
  }, []);

  const clearResources = () => {
    clearTimeout(pollingTimeoutRef.current);
    pollingTimeoutRef.current = null;
    dispatch(setIntegrationStatus(null));
    dispatch(setIntegrationLoading(false));
  };

  const handleZagsPolling = () => {
    dispatch(
      enableIntegrationForServices({
        close: integrationModal.close,
        onStatus: async status => {
          if (status === 'PENDING') {
            pollingTimeoutRef.current = setTimeout(() => {
              if (integrationModal.isOpen) {
                handleZagsPolling();
              }
            }, 10000);
          } else if (status === 'VALID') {
            clearResources();
            integrationModal.close();
            Notice.success(t('serviceRequest_zagsIntegrationRetry'));
            await requestForChildren();
          }
        }
      })
    );
  };

  const onZagsAutoFill = () => {
    setShowHelperText(false);
    handleZagsPolling();
  };

  const onDonIntegration = () => {
    setShowHelperText(true);
    clearResources();
  };

  useEffect(() => {
    return () => {
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
      }
    };
  }, []);

  const selectChildren = children => {
    changeFormValue(`${metadataKey}.child_iin`, children['iin']);

    changeFormValue(`${metadataKey}.child_surname`, children['lastname']);
    changeFormValue(`${metadataKey}.child_name`, children['firstname']);
    changeFormValue(`${metadataKey}.child_middlename`, children['middlename']);
    changeFormValue(`${metadataKey}.child_birthday`, children['birthday']);
    close();
  };
  return (
    <>
      {integrationModal.isOpen && (
        <IntegrationModal
          title="kindergarten_integrationTitle"
          {...integrationModal}
          onDonIntegration={onDonIntegration}
          onZagsInit={onZagsAutoFill}
          loadingIntegration={loadingIntegration}
          helperText={showHelperText}
          kdpGbdflStatus={kdpGbdflStatus}
          kdpZagsStatus={kdpZagsStatus}
          clearResources={clearResources}
          setShowHelperText={setShowHelperText}
        />
      )}
      <Modal
        open={isOpen && !integrationModal.isOpen}
        onClose={close}
        title={t('serviceRequest_listChildren')}
      >
        <DialogContent dividers>
          <Table
            loading={loading}
            columns={[
              {
                Header: t('serviceProviders_fioChild'),
                filterable: false,
                Cell: ({ original }) => {
                  const { lastname, firstname, middlename } = original;
                  return [lastname, firstname, middlename].filter(Boolean).join(' ');
                }
              },
              {
                Header: t('serviceProviders_columnIin'),
                filterable: false,
                width: 150,
                Cell: ({ original }) => original.iin
              }
            ]}
            onClickRow={({ original }) => selectChildren(original)}
            data={listChildren}
          />
        </DialogContent>
      </Modal>
    </>
  );
}

export default ListChildrenModalForServices;
